import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ref, push } from 'firebase/database'
import { database } from '../config/firebase'
import { useLanguage } from '../contexts/LanguageContext'
import Navbar from '../components/Navbar/Navbar'
import Footer from '../components/Footer/Footer'
import styles from './CustomSiteBuilder.module.css'

interface Module {
  id: string
  name: string
  description: string
  price: number
  category: 'basic' | 'feature' | 'integration' | 'management'
}

const getModules = (t: (key: string) => string): Module[] => [
  {
    id: 'seo',
    name: t('builder.module.seo'),
    description: t('builder.module.seoDesc'),
    price: 800,
    category: 'feature'
  },
  {
    id: 'appointment',
    name: t('builder.module.appointment'),
    description: t('builder.module.appointmentDesc'),
    price: 1000,
    category: 'feature'
  },
  {
    id: 'contact-email',
    name: t('builder.module.contactEmail'),
    description: t('builder.module.contactEmailDesc'),
    price: 100,
    category: 'basic'
  },
  {
    id: 'whatsapp',
    name: t('builder.module.whatsapp'),
    description: t('builder.module.whatsappDesc'),
    price: 200,
    category: 'integration'
  },
  {
    id: 'admin-panel',
    name: t('builder.module.adminPanel'),
    description: t('builder.module.adminPanelDesc'),
    price: 1000,
    category: 'management'
  },
  {
    id: 'multi-language',
    name: t('builder.module.multiLanguage'),
    description: t('builder.module.multiLanguageDesc'),
    price: 700,
    category: 'feature'
  },
  {
    id: 'blog',
    name: t('builder.module.blog'),
    description: t('builder.module.blogDesc'),
    price: 500,
    category: 'feature'
  },
  {
    id: 'gallery',
    name: t('builder.module.gallery'),
    description: t('builder.module.galleryDesc'),
    price: 300,
    category: 'feature'
  },
  {
    id: 'newsletter',
    name: t('builder.module.newsletter'),
    description: t('builder.module.newsletterDesc'),
    price: 600,
    category: 'feature'
  },
  {
    id: 'live-chat',
    name: t('builder.module.liveChat'),
    description: t('builder.module.liveChatDesc'),
    price: 800,
    category: 'integration'
  },
  {
    id: 'analytics',
    name: t('builder.module.analytics'),
    description: t('builder.module.analyticsDesc'),
    price: 300,
    category: 'feature'
  },
  {
    id: 'social-media',
    name: t('builder.module.socialMedia'),
    description: t('builder.module.socialMediaDesc'),
    price: 200,
    category: 'integration'
  },
  {
    id: 'map',
    name: t('builder.module.map'),
    description: t('builder.module.mapDesc'),
    price: 400,
    category: 'integration'
  },
  {
    id: 'video',
    name: t('builder.module.video'),
    description: t('builder.module.videoDesc'),
    price: 500,
    category: 'feature'
  },
  {
    id: 'testimonials',
    name: t('builder.module.testimonials'),
    description: t('builder.module.testimonialsDesc'),
    price: 300,
    category: 'feature'
  },
  {
    id: 'faq',
    name: t('builder.module.faq'),
    description: t('builder.module.faqDesc'),
    price: 100,
    category: 'basic'
  },
  {
    id: 'search',
    name: t('builder.module.search'),
    description: t('builder.module.searchDesc'),
    price: 700,
    category: 'feature'
  }
]

const hexToRgba = (hex: string, alpha: string) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const a = parseFloat(alpha) / 100
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

const CustomSiteBuilder = () => {
  const { t } = useLanguage()
  const [searchParams] = useSearchParams()
  const packageType = searchParams.get('type')
  
  const modules = getModules(t)
  
  // Paket tipine göre otomatik seçili modüller
  const getDefaultModules = (): Set<string> => {
    const defaultModules = new Set<string>()
    
    if (packageType === 'e-ticaret') {
      // E-Ticaret sitesinde zaten olanlar
      defaultModules.add('admin-panel') // Admin paneli otomatik
      defaultModules.add('search') // Gelişmiş arama
      defaultModules.add('gallery') // Ürün galerisi
    } else if (packageType === 'urun-yonetim') {
      // İçerik yönetim sitesinde zaten olanlar
      defaultModules.add('admin-panel') // Admin paneli otomatik
      defaultModules.add('search') // Gelişmiş arama
      defaultModules.add('gallery') // İçerik galerisi
    }
    
    return defaultModules
  }
  
  const [selectedModules, setSelectedModules] = useState<Set<string>>(getDefaultModules())
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    companyName: '',
    companyDescription: '',
    domain: '',
    theme: 'dark',
    colors: ['#667eea']
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  // Paket içinde zaten olan modüller (kaldırılamaz)
  const getIncludedModules = (): Set<string> => {
    const included = new Set<string>()
    
    if (packageType === 'e-ticaret') {
      included.add('admin-panel')
      included.add('payment')
      included.add('search')
      included.add('gallery')
    } else if (packageType === 'urun-yonetim') {
      included.add('admin-panel')
      included.add('search')
      included.add('gallery')
    }
    
    return included
  }
  
  const includedModules = getIncludedModules()
  
  const toggleModule = (moduleId: string) => {
    // Eğer modül paket içinde zaten varsa, kaldırılamaz
    if (includedModules.has(moduleId)) {
      return
    }
    
    setSelectedModules((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }

  const getTotalPrice = () => {
    // Fiyat gösterilmediği için 0 döndürülüyor
    return 0
  }

  const getModulesByCategory = (category: Module['category']) => {
    return modules.filter(module => module.category === category)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.firstName || !formData.lastName || !formData.phone) {
      alert(t('builder.alert.fillFields'))
      return
    }

    setIsSubmitting(true)

    try {
      const selectedModulesList = modules.filter(module => selectedModules.has(module.id))
      
      // Detaylı sipariş mesajı oluştur
      let message = '═══════════════════════════════════════\n'
      message += '🎨 ÖZEL SİTE OLUŞTURMA SİPARİŞİ\n'
      message += '═══════════════════════════════════════\n\n'
      
      message += '📋 ŞİRKET BİLGİLERİ\n'
      message += '───────────────────────────────────────\n'
      message += `Şirket Adı: ${formData.companyName || 'Belirtilmemiş'}\n`
      message += `Şirket Açıklaması: ${formData.companyDescription || 'Belirtilmemiş'}\n`
      message += `Alan Adı (Domain): ${formData.domain || 'Belirtilmemiş'}\n\n`
      
      message += '🎨 TASARIM TERCİHLERİ\n'
      message += '───────────────────────────────────────\n'
      message += `Tema: ${formData.theme === 'dark' ? '🌙 Koyu Tema' : '☀️ Açık Tema'}\n`
      message += `Seçilen Renkler (${formData.colors.length} adet):\n`
      formData.colors.forEach((color, index) => {
        message += `  ${index + 1}. ${color}\n`
      })
      message += '\n'
      
      message += '⚙️ SEÇİLEN MODÜLLER\n'
      message += '───────────────────────────────────────\n'
      if (selectedModulesList.length > 0) {
        selectedModulesList.forEach((module, index) => {
          message += `${index + 1}. ${module.name}\n`
          message += `   Açıklama: ${module.description}\n\n`
        })
      } else {
        message += 'Seçilen modül bulunmamaktadır.\n\n'
      }
      
      message += '👤 İLETİŞİM BİLGİLERİ\n'
      message += '───────────────────────────────────────\n'
      message += `Ad Soyad: ${formData.firstName} ${formData.lastName}\n`
      message += `Telefon: ${formData.phone}\n`
      message += `E-posta: ${formData.email || 'Belirtilmemiş'}\n\n`
      
      message += '═══════════════════════════════════════\n'
      message += `Sipariş Tarihi: ${new Date().toLocaleString('tr-TR')}\n`
      message += '═══════════════════════════════════════'

      const orderData = {
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        service: 'Özel Site Oluşturma',
        message: message,
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleString('tr-TR'),
        status: 'Sipariş Aşamada',
        teklifUcreti: 0,
        kazanilanRakam: getTotalPrice()
      }

      const contactsRef = ref(database, 'contacts')
      await push(contactsRef, orderData)

      setSubmitSuccess(true)
      setSelectedModules(new Set())
      setFormData({ 
        firstName: '', 
        lastName: '', 
        phone: '', 
        email: '',
        companyName: '',
        companyDescription: '',
        domain: '',
        theme: 'dark',
        colors: ['#667eea']
      })

      setTimeout(() => {
        setSubmitSuccess(false)
        window.location.href = '/'
      }, 3000)
    } catch (error) {
      console.error('Error submitting order:', error)
      alert(t('builder.alert.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Navbar />
      <section className={styles.builderSection}>
        <div className={styles.container}>
          <div className={styles.header}>
            <p className={styles.pill}>{t('builder.pill')}</p>
            <h1 className={styles.title}>{t('builder.title')}</h1>
            <p className={styles.subtitle}>
              {t('builder.subtitle')}
            </p>
          </div>

          <div className={styles.builderContent}>
            <div className={styles.modulesSection}>
              <div className={styles.modulesContainer}>
                <div className={styles.moduleCategory}>
                  <h3 className={styles.categoryTitle}>{t('builder.category.siteInfo')}</h3>
                  <div 
                    className={styles.siteInfoContent}
                    style={formData.colors.length > 1 
                      ? { 
                          background: `linear-gradient(135deg, ${formData.colors.map((c, i) => {
                            const opacity = i === 0 ? '25' : i === formData.colors.length - 1 ? '15' : '20'
                            return hexToRgba(c, opacity)
                          }).join(', ')})`,
                          borderColor: hexToRgba(formData.colors[0], '40')
                        }
                      : { 
                          backgroundColor: formData.colors[0] ? hexToRgba(formData.colors[0], '15') : 'rgba(255, 255, 255, 0.05)',
                          borderColor: formData.colors[0] ? hexToRgba(formData.colors[0], '30') : 'rgba(255, 255, 255, 0.1)'
                        }
                    }
                  >
                    <div className={styles.formGroup}>
                      <label htmlFor="companyName">{t('builder.companyName')} *</label>
                      <input
                        type="text"
                        id="companyName"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        required
                        placeholder={t('builder.companyNamePlaceholder')}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="companyDescription">{t('builder.companyDescription')} *</label>
                      <textarea
                        id="companyDescription"
                        value={formData.companyDescription}
                        onChange={(e) => {
                          if (e.target.value.length <= 300) {
                            setFormData({ ...formData, companyDescription: e.target.value })
                          }
                        }}
                        required
                        placeholder={t('builder.companyDescriptionPlaceholder')}
                        maxLength={300}
                        rows={3}
                        className={styles.textarea}
                      />
                      <p className={styles.inputHint}>
                        {t('builder.companyDescriptionHint')}
                      </p>
                      <span className={styles.charCount}>
                        {formData.companyDescription.length}/300
                      </span>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="domain">{t('builder.domain')} *</label>
                      <input
                        type="text"
                        id="domain"
                        value={formData.domain}
                        onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                        required
                        placeholder={t('builder.domainPlaceholder')}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="theme">{t('builder.theme')} *</label>
                      <div className={styles.themeSelector}>
                        <button
                          type="button"
                          className={`${styles.themeOption} ${formData.theme === 'dark' ? styles.active : ''}`}
                          onClick={() => setFormData({ ...formData, theme: 'dark' })}
                        >
                          <div className={styles.themePreview} style={{ background: '#1a1a1a', color: '#ffffff' }}>
                            <div className={styles.themeDot} style={{ background: '#ffffff' }}></div>
                            <span>{t('builder.themeDark')}</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          className={`${styles.themeOption} ${formData.theme === 'light' ? styles.active : ''}`}
                          onClick={() => setFormData({ ...formData, theme: 'light' })}
                        >
                          <div className={styles.themePreview} style={{ background: '#ffffff', color: '#000000' }}>
                            <div className={styles.themeDot} style={{ background: '#000000' }}></div>
                            <span>{t('builder.themeLight')}</span>
                          </div>
                        </button>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label>{t('builder.colors')}</label>
                      <div className={styles.colorsContainer}>
                        {formData.colors.map((color, index) => (
                          <div 
                            key={index} 
                            className={styles.colorSquare}
                            style={{ backgroundColor: color }}
                          >
                            <input
                              type="color"
                              value={color}
                              onChange={(e) => {
                                const newColors = [...formData.colors]
                                newColors[index] = e.target.value
                                setFormData({ ...formData, colors: newColors })
                              }}
                              className={styles.colorPickerSquare}
                            />
                            {formData.colors.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newColors = formData.colors.filter((_, i) => i !== index)
                                  setFormData({ ...formData, colors: newColors })
                                }}
                                className={styles.removeColorBtn}
                                title={t('builder.removeColor')}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                        {formData.colors.length < 5 && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, colors: [...formData.colors, '#667eea'] })
                            }}
                            className={styles.addColorBtn}
                          >
                            +
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.moduleCategory}>
                  <h3 className={styles.categoryTitle}>{t('builder.category.basic')}</h3>
                  <div className={styles.modulesGrid}>
                    {getModulesByCategory('basic').map((module) => {
                      const isIncluded = includedModules.has(module.id)
                      const isSelected = selectedModules.has(module.id)
                      return (
                      <label key={module.id} className={`${styles.moduleCard} ${isIncluded ? styles.includedModule : ''}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleModule(module.id)}
                          disabled={isIncluded}
                          className={styles.moduleCheckbox}
                        />
                        <div className={styles.moduleContent}>
                          <div className={styles.moduleHeader}>
                            <span className={styles.moduleName}>{module.name}</span>
                          </div>
                          <p className={styles.moduleDescription}>{module.description}</p>
                        </div>
                      </label>
                      )
                    })}
                  </div>
                </div>

                <div className={styles.moduleCategory}>
                  <h3 className={styles.categoryTitle}>{t('builder.category.features')}</h3>
                  <div className={styles.modulesGrid}>
                    {getModulesByCategory('feature').map((module) => {
                      const isIncluded = includedModules.has(module.id)
                      const isSelected = selectedModules.has(module.id)
                      return (
                      <label key={module.id} className={`${styles.moduleCard} ${isIncluded ? styles.includedModule : ''}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleModule(module.id)}
                          disabled={isIncluded}
                          className={styles.moduleCheckbox}
                        />
                        <div className={styles.moduleContent}>
                          <div className={styles.moduleHeader}>
                            <span className={styles.moduleName}>{module.name}</span>
                          </div>
                          <p className={styles.moduleDescription}>{module.description}</p>
                        </div>
                      </label>
                      )
                    })}
                  </div>
                </div>

                <div className={styles.moduleCategory}>
                  <h3 className={styles.categoryTitle}>{t('builder.category.integrations')}</h3>
                  <div className={styles.modulesGrid}>
                    {getModulesByCategory('integration').map((module) => {
                      const isIncluded = includedModules.has(module.id)
                      const isSelected = selectedModules.has(module.id)
                      return (
                      <label key={module.id} className={`${styles.moduleCard} ${isIncluded ? styles.includedModule : ''}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleModule(module.id)}
                          disabled={isIncluded}
                          className={styles.moduleCheckbox}
                        />
                        <div className={styles.moduleContent}>
                          <div className={styles.moduleHeader}>
                            <span className={styles.moduleName}>{module.name}</span>
                          </div>
                          <p className={styles.moduleDescription}>{module.description}</p>
                        </div>
                      </label>
                      )
                    })}
                  </div>
                </div>

                <div className={styles.moduleCategory}>
                  <h3 className={styles.categoryTitle}>{t('builder.category.management')}</h3>
                  <div className={styles.modulesGrid}>
                    {getModulesByCategory('management').map((module) => {
                      const isIncluded = includedModules.has(module.id)
                      const isSelected = selectedModules.has(module.id)
                      return (
                      <label key={module.id} className={`${styles.moduleCard} ${isIncluded ? styles.includedModule : ''}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleModule(module.id)}
                          disabled={isIncluded}
                          className={styles.moduleCheckbox}
                        />
                        <div className={styles.moduleContent}>
                          <div className={styles.moduleHeader}>
                            <span className={styles.moduleName}>{module.name}</span>
                          </div>
                          <p className={styles.moduleDescription}>{module.description}</p>
                        </div>
                      </label>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.orderSection}>
              <form onSubmit={handleSubmit} className={styles.orderForm}>
                <h2 className={styles.formTitle}>{t('builder.contactInfo')}</h2>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="firstName">{t('builder.firstName')} *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      placeholder={t('builder.firstNamePlaceholder')}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="lastName">{t('builder.lastName')} *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      placeholder={t('builder.lastNamePlaceholder')}
                    />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="phone">{t('builder.phone')} *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      placeholder={t('builder.phonePlaceholder')}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">{t('builder.email')}</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('builder.emailPlaceholder')}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={isSubmitting || submitSuccess}
                >
                  {isSubmitting ? (
                    <>
                      <svg className={styles.spinner} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                      </svg>
                      <span>{t('builder.submitting')}</span>
                    </>
                  ) : submitSuccess ? (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>{t('builder.submitSuccess')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('builder.submit')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default CustomSiteBuilder

